import ChevronLeft from "@mui/icons-material/ChevronLeft";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { hexToRgba, rgbaToHex } from "csi/utility/colorUtility";
import Button from "designSystem/components/button/Button";
import { GridRow } from "designSystem/components/grid/GridContainer";
import ColorPaletteIcon from "designSystem/components/icon/ColorPaletteIcon";
import Link from "designSystem/components/link/Link";
import Text from "designSystem/components/text/Text";
import TextField from "designSystem/components/textfield/TextField";
import { red } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import React, { ChangeEvent, FunctionComponent, useCallback, useMemo, useState } from "react";
import { RgbColorPicker, RgbaColorPicker } from "react-colorful";
import css from "./ColorPicker.module.css";
import DefaultColors from "./DefaultColors";
import RecentColors from "./RecentColors";

export const TRANSPARENT = "transparent";
export const CIRCLE_DEFAULT_COLORS = [
	TRANSPARENT, "#FFFFFF", "#EEEEEE", "#D9D9D9", "#969696", "#525252", "#363636", "#000000",
	"#FFCDD3", "#F8BAD0", "#FFE0B1", "#FFF9C4", "#DCECC8", "#B2EAF2", "#BBDEFB", "#E0BEE7",
	"#E47373", "#F06292", "#FFB74D", "#FFF076", "#AED580", "#4ED0E1", "#64B5F6", "#BA68C8",
	"#F44336", "#E91E62", "#FF9800", "#FFEB3B", "#8BC24B", "#00BCD4", "#2196F3", "#9D27B0",
	"#D3302F", "#C2175B", "#F47D02", "#FBBF2D", "#679F37", "#0097A7", "#1876D1", "#7B1EA2",
	"#B71D1C", "#870E4F", "#E65000", "#F57F16", "#33691D", "#035F64", "#0D47A1", "#4A148C",
];

export type BackgroundColorHandlerFunction = (color: string, recentCustomColors?: string[]) => void;
export type OnPageChangeHandler = (isMoreColorsPage: boolean) => void;

export interface ColorPickerProps {
	/** the selected color. */
	color: string;

	/** circle default colors */
	defaultColors?: string[];

	/** display the Alpha slider and Alpha input field */
	displayAlpha?: boolean;

	/** a toggleable value that will force a full component re-render.
	   Required when the component resizes, and its position must be updated
	   to accommodate the new size. */
	forceColorPickerUpdate?: boolean;

	/** the callback for when the user selects a color. */
	onColorChange: BackgroundColorHandlerFunction;

	/** the parent handler for when the user changes to/from the More Colors page. */
	onPageChange?: OnPageChangeHandler;

	/** List of recent colors, in hex. */
	recentCustomColors?: string[];
}

const validColorInputs = {
	red: {
		min: 0,
		max: 255,
		step: 1,
	},
	green: {
		min: 0,
		max: 255,
		step: 1,
	},
	blue: {
		min: 0,
		max: 255,
		step: 1,
	},
	alpha: {
		min:  0,
		max:  1,
		step: 0.01,
	},
};

const HexColorPickerStyled = styled(RgbaColorPicker)(({ theme }) => ({
	"& .react-colorful__hue-pointer": {
		border: `2px solid ${theme.palette.FocusPrimary}`,
	},
	"& .react-colorful__alpha-pointer": {
		border: `2px solid ${theme.palette.FocusPrimary}`,
	},
}));

const RgbColorPickerStyled = styled(RgbColorPicker)(({ theme }) => ({
	"& .react-colorful__hue-pointer": {
		border: `2px solid ${theme.palette.FocusPrimary}`,
	},
	"& .react-colorful__alpha-pointer": {
		border: `2px solid ${theme.palette.FocusPrimary}`,
	},
}));

export interface ColorPickerPageProps extends Omit<ColorPickerProps, "onPageChange"> {
	/* function to set the 'More Colors' state */
	onPageChange?: (moreColors: boolean) => void;
}

const MoreColorsPage: FunctionComponent<ColorPickerPageProps> = (props) => {
	const { color, displayAlpha, onColorChange, recentCustomColors, onPageChange } = props;

	const rgbaColor = useMemo(() => hexToRgba(color), [color]);

	const {
		red: colorRed,
		green: colorGreen,
		blue: colorBlue,
		alpha: colorAlpha,
	} = rgbaColor || {};

	const onHexValueChange = (event: ChangeEvent<HTMLInputElement>): void => onColorChange(`#${event.target.value}`);

	const onRgbaValueChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rgba: string): void => {
		let value = rgba === "alpha" ? parseFloat(event.target.value) : parseInt(event.target.value);

		// validate the input value and clamp it to the valid range
		const { max, min } = validColorInputs[rgba];
		if (value < min || value > max) {
			value = Math.min(Math.max(value, min), max);
		}
		
		onColorChange(rgbaToHex({ ...rgbaColor, [rgba]: value }));
	};

	const hexValue = color === "transparent" ? "" : color.replace("#", "")
		.toUpperCase();

	const baseTextFieldCss = {
		marginRight: "4px",
		minWidth: "50px",
	};

	const colorsForHEXPickerComponent = {
		r: colorRed,
		g: colorGreen,
		b: colorBlue,
		a: colorAlpha,
	};

	const textFieldOnChangeAlpha = (e) => onRgbaValueChange(e, "alpha");
	const textFieldOnChangeRed = (e) => onRgbaValueChange(e, "red");
	const textFieldOnChangeGreen = (e) => onRgbaValueChange(e, "green");
	const textFieldOnChangeBlue = (e) => onRgbaValueChange(e, "blue");
	const textColorPickerChange = (color) => onColorChange(rgbaToHex({
		red: color.r,
		green: color.g,
		blue: color.b,
		alpha: color.a,
	}));

	return (
		<>
			<GridRow
				spacing={1}
				sx={{
					padding: "2px 10px 2px",
				}}
			>
				<Button
					design="link"
					onClick={() => onPageChange(false)}
					res="Back"
					startIcon={<ChevronLeft />}
					sx={{
						fontWeight: 600,
					}}
				/>
			</GridRow>
			<Box
				onKeyDown={(e) => e.stopPropagation()}
				sx={{
					borderBottom: STANDARD_BORDER_STYLE,
					padding: "0 16px",
				}}
			>
				{ displayAlpha ? (
					<HexColorPickerStyled
						className={css.centricRgbColorPicker}
						color={colorsForHEXPickerComponent}
						onChange={textColorPickerChange}
					/>
				) : (
					<RgbColorPickerStyled
						className={css.centricRgbColorPicker}
						color={colorsForHEXPickerComponent}
						onChange={textColorPickerChange}
					/>
				)
				}
				<Box
					sx={{
						display: "flex",
					}}
				>
					<TextField
						onChange={onHexValueChange}
						placeholderRes="HEX"
						res="HEX"
						sx={{
							...baseTextFieldCss,
							minWidth: displayAlpha ? "6em" : "5em",
						}}
						value={hexValue}
					/>
					<TextField
						inputProps={validColorInputs["red"]}
						onChange={textFieldOnChangeRed}
						placeholderRes="Red"
						res="Red"
						sx={baseTextFieldCss}
						type="number"
						value={colorRed}
					/>
					<TextField
						inputProps={validColorInputs["green"]}
						onChange={textFieldOnChangeGreen}
						placeholderRes="Green"
						res="Green"
						sx={baseTextFieldCss}
						type="number"
						value={colorGreen}
					/>
					<TextField
						inputProps={validColorInputs["blue"]}
						onChange={textFieldOnChangeBlue}
						placeholderRes="Blue"
						res="Blue"
						sx={{
							...baseTextFieldCss,
							marginRight: displayAlpha ? "4px" : "0px",
						}}
						type="number"
						value={colorBlue}
					/>
					{ displayAlpha && (
						<TextField
							inputProps={{
								...validColorInputs["alpha"],
								inputMode: "decimal",
							}}
							onChange={textFieldOnChangeAlpha}
							placeholderRes="Alpha"
							res="Alpha"
							sx={{
								...baseTextFieldCss,
								marginRight: "0px",
							}}
							type="number"
							value={colorAlpha}
						/>
					)
					}
				</Box>
			</Box>
			<Box
				sx={{
					padding: "8px 12px 0px",
				}}
			>
				<RecentColors colors={recentCustomColors} onColorChange={onColorChange} />
			</Box>
		</>
	);
};

const DefaultColorsPage: FunctionComponent<ColorPickerPageProps> = (props) => {
	const { defaultColors, color, onColorChange, onPageChange } = props;
	return (
		<Box>
			<Box padding="4px 12px 6px">
				<DefaultColors
					color={color}
					colors={defaultColors || CIRCLE_DEFAULT_COLORS}
					onColorChange={onColorChange}
				/>
			</Box>
			<Box
				sx={{
					textAlign: "left",
					borderTop: STANDARD_BORDER_STYLE,
					padding: "8px 14px 9px",
				}}
			>
				<ColorPaletteIcon />
				<Link
					onClick={(e) => {
						e.preventDefault();
						onPageChange(true);
					}}
					sx={{ marginLeft: "6px" }}
				>
					<Text res="MoreColors" />
				</Link>
			</Box>
		</Box>
	);
};

const ColorPicker: FunctionComponent<ColorPickerProps> = (props) => {
	const { defaultColors, displayAlpha, color, recentCustomColors, onColorChange, onPageChange } = props;
	const [moreColors, setMoreColors] = useState(false);

	const onCircleColorChange = useCallback((color: string) => {
		onColorChange(color);
	}, [onColorChange]);

	const onMoreColorsPageChange = (isMoreColorsPage: boolean) => {
		setMoreColors(isMoreColorsPage);
		onPageChange?.(isMoreColorsPage);
	};

	return (
		<Box
			onClick={(e) => e.stopPropagation()}
			sx={{
				width: "min-content",
			}}
		>
			{moreColors ? (
				<MoreColorsPage
					color={color}
					displayAlpha={displayAlpha}
					onColorChange={onCircleColorChange}
					onPageChange={onMoreColorsPageChange}
					recentCustomColors={recentCustomColors}
				/>
			)
				: (
					<DefaultColorsPage
						color={color}
						defaultColors={defaultColors || CIRCLE_DEFAULT_COLORS}
						onColorChange={onCircleColorChange}
						onPageChange={onMoreColorsPageChange}
					/>
				)}
		</Box>
	);
};

export const cssCheckeredBackground = "url(\"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAIAAAAlC+aJAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAEnQAABJ0Ad5mH3gAAAD/SURBVGhD7Y8xCgQxEMPu/z/NJ3avUeVG3jJgIQgpxODf+cjzPp8kq4lc3QCTrCZydQNMsprI1Q0wyWoiVzfAJKuJXN0Ak6wmcnUDTLKayNUNMMlqIlfvHxB/lTs1katkNRtgRK6S1WyAEblKVrMBRuQqWc0GGJGrZDUbYESuktVsgBG5SlazAUbkKlnN/QN4a+KeSlYTuboBJllN5OoGmGQ1kasbYJLVRK5ugElWE7m6ASZZTeTqBphkNZGrG2CS1USu3j8g/ip3aiJXyWo2wIhcJavZACNylaxmA4zIVbKaDTAiV8lqNsCIXCWr2QAjcpWsZgOMyFWymssHnPMHySqypI/MXCUAAAAASUVORK5CYII=\") center/100%";

export const TransparentSwatch = styled(Box)({
	border: `1px solid ${red[600]}`,
	position: "absolute",
	top: "8px",
	transform: "rotate(135deg)",
	width: "18px",
});

export default ColorPicker;
